import { useState, useEffect, useMemo } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import productService from '../../services/productService';
import brandService from '../../services/brandService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import ProductModal from './ProductModal';
import ViewProductModal from './ViewProductModal';

function isBrandActive(product) {
  return product.brandIsActive === true;
}

function isVisibleOnStore(product) {
  const published = product.isPublished !== false;
  return published && isBrandActive(product);
}

function storeVisibilityLabel(product, isRtl) {
  if (isVisibleOnStore(product)) {
    return isRtl ? 'ظاهر' : 'Visible';
  }
  const brandHidden = !isBrandActive(product);
  const productHidden = product.isPublished === false;
  if (brandHidden && productHidden) return isRtl ? 'مخفي · منتج وعلامة' : 'Hidden · product & brand';
  if (brandHidden) return isRtl ? 'مخفي · علامة تجارية' : 'Hidden · brand';
  return isRtl ? 'مخفي · منتج' : 'Hidden · product';
}

const STORE_FILTERS = ['all', 'visible', 'hidden'];

const ProductsIndex = () => {
  const { get, isRtl } = useLocalization();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [storeFilter, setStoreFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const enrichProductsWithBrandStatus = (data, brands) => {
    const activeByBrandId = new Map(
      (brands || []).map((b) => [String(b.id).toLowerCase(), b.isActive === true])
    );
    return (data || []).map((p) => {
      const brandKey = p.brandId ? String(p.brandId).toLowerCase() : '';
      const fromBrandsList = brandKey && activeByBrandId.has(brandKey)
        ? activeByBrandId.get(brandKey)
        : null;
      const brandIsActive =
        fromBrandsList !== null ? fromBrandsList : p.brandIsActive !== false;
      return {
        ...p,
        brandIsActive,
        visibleOnStore: p.isPublished !== false && brandIsActive,
      };
    });
  };

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const [data, brands] = await Promise.all([
        productService.getAll(),
        brandService.getAll().catch(() => []),
      ]);
      setProducts(enrichProductsWithBrandStatus(data, brands));
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await productService.exportToExcel();
    } catch (error) {
      console.error('Error exporting products:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProductId(product.id);
    setShowProductModal(true);
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setShowViewModal(true);
  };

  const handleToggleVisibility = async (product) => {
    const isPublished = product.isPublished !== false;
    const nextVisible = !isPublished;
    const msg = nextVisible
      ? (isRtl ? 'إظهار هذا العطر في المتجر؟' : 'Show this perfume on the store?')
      : (isRtl ? 'إخفاء هذا العطر من المتجر؟' : 'Hide this perfume from the store?');
    if (!window.confirm(msg)) return;
    try {
      await productService.setStoreVisibility(product.id, nextVisible);
      loadProducts();
    } catch (error) {
      console.error('Error updating product visibility:', error);
      alert(isRtl ? 'تعذر تحديث الحالة' : 'Failed to update visibility');
    }
  };

  const filteredProducts = products.filter((product) => {
    const onStore = isVisibleOnStore(product);
    if (storeFilter === 'visible' && !onStore) return false;
    if (storeFilter === 'hidden' && onStore) return false;
    if (brandFilter !== 'all' && String(product.brandId).toLowerCase() !== brandFilter) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.nameEn?.toLowerCase().includes(search) ||
      product.nameAr?.toLowerCase().includes(search) ||
      product.brandEn?.toLowerCase().includes(search) ||
      product.categoryNameEn?.toLowerCase().includes(search)
    );
  });

  // Brands that actually have products, with their product counts (taken from the loaded list: no extra request).
  const brandOptions = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (!p.brandId) return;
      const key = String(p.brandId).toLowerCase();
      const cur = map.get(key) || { key, name: p.brandEn || '—', count: 0 };
      cur.count += 1;
      map.set(key, cur);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const visibleOnStoreCount = products.filter((p) => isVisibleOnStore(p)).length;
  const hiddenOnStoreCount = products.length - visibleOnStoreCount;

  const columns = [
    {
      header: isRtl ? 'المنتج' : 'Product',
      accessor: 'nameEn',
      render: (value, row) => {
        const imageUrl = getProductImageUrl(row.imageUrlFull || row.imageUrl);
        return (
          <div className="product-cell">
            <div className="product-thumb-wrap">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={value}
                  className="product-thumb"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.add('visible'); }}
                />
              ) : null}
              <div className={`product-thumb-placeholder ${!imageUrl ? 'visible' : ''}`} aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
            </div>
            <div className="product-info">
              <div className="product-name">{isRtl ? row.nameAr : row.nameEn}</div>
              {isRtl && row.nameEn && <div className="product-name-sub">{row.nameEn}</div>}
            </div>
          </div>
        );
      },
    },
    {
      header: isRtl ? 'الفئة' : 'Category',
      accessor: 'categoryNameEn',
      render: (value) => <span className="text-secondary">{value || '-'}</span>
    },
    {
      header: isRtl ? 'العلامة التجارية' : 'Brand',
      accessor: 'brandEn',
      render: (value) => <span className="text-secondary">{value || '-'}</span>
    },
    {
      header: isRtl ? 'المتجر' : 'Store',
      accessor: 'isPublished',
      render: (_, row) => {
        const onStore = isVisibleOnStore(row);
        return (
          <span className={`badge ${onStore ? 'badge-success' : 'badge-warning'}`}>
            {storeVisibilityLabel(row, isRtl)}
            {!row.hasVariants ? (isRtl ? ' · بلا متغيرات' : ' · no variants') : ''}
          </span>
        );
      },
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (_, row) => (
        <div className="action-buttons">
          <Button
            size="small"
            variant={row.isPublished !== false ? 'secondary' : 'primary'}
            onClick={() => handleToggleVisibility(row)}
            title={
              !isBrandActive(row)
                ? (isRtl ? 'إظهار العلامة التجارية مطلوب لظهور المنتج في المتجر' : 'Brand must be visible on store for product to appear')
                : undefined
            }
          >
            {row.isPublished !== false ? (isRtl ? 'إخفاء' : 'Hide') : (isRtl ? 'إظهار' : 'Show')}
          </Button>
          <Button size="small" variant="secondary" onClick={() => handleView(row)}>
            {get('view')}
          </Button>
          <Button size="small" variant="primary" onClick={() => handleEdit(row)}>
            {get('edit')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading products...'} />;
  }

  return (
    <div className="products-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{get('product_management')}</h1>
            <p className="page-description">{get('product_management_desc')}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="secondary" onClick={handleExport}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span className="hidden-mobile">{get('export_excel')}</span>
            </Button>
            <Button variant="primary" onClick={() => {
              setEditingProductId(null);
              setShowProductModal(true);
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>{get('add_new_product')}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{products.length}</div>
              <div className="stat-label">{get('total_products')}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{products.filter(p => p.hasVariants).length}</div>
              <div className="stat-label">{isRtl ? 'مع متغيرات' : 'With Variants'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{visibleOnStoreCount}</div>
              <div className="stat-label">{isRtl ? 'ظاهر في المتجر' : 'Visible on store'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                <line x1="1" y1="1" x2="23" y2="23"></line>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{hiddenOnStoreCount}</div>
              <div className="stat-label">{isRtl ? 'مخفي من المتجر' : 'Hidden from store'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table Card */}
      <div className="card">
        <div className="card-header">
          <div className="search-box">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن المنتجات...' : 'Search products...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="card-header-info hidden-mobile">
            <span className="text-muted">
              {isRtl ? `${filteredProducts.length} منتج` : `${filteredProducts.length} products`}
            </span>
          </div>
        </div>
        <div className="products-store-filter-bar">
          {STORE_FILTERS.map((key) => {
            const active = storeFilter === key;
            const labels = {
              all: isRtl ? 'الكل' : 'All',
              visible: isRtl ? 'ظاهر في المتجر' : 'Visible on store',
              hidden: isRtl ? 'مخفي من المتجر' : 'Hidden from store',
            };
            return (
              <button
                key={key}
                type="button"
                className={`status-tab ${active ? 'status-tab-active' : ''}`}
                onClick={() => setStoreFilter(key)}
              >
                {labels[key]}
              </button>
            );
          })}
          <div className="products-brand-filter">
            <select
              className="form-select"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              aria-label={isRtl ? 'تصفية حسب العلامة التجارية' : 'Filter by brand'}
            >
              <option value="all">{isRtl ? 'كل العلامات التجارية' : 'All brands'}</option>
              {brandOptions.map((b) => (
                <option key={b.key} value={b.key}>{`${b.name} (${b.count})`}</option>
              ))}
            </select>
            {brandFilter !== 'all' ? (
              <button type="button" className="btn btn-ghost btn-small" onClick={() => setBrandFilter('all')}>
                {isRtl ? 'مسح' : 'Clear'}
              </button>
            ) : null}
          </div>
        </div>
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredProducts}
            emptyMessage={isRtl ? 'لا توجد منتجات' : 'No products found'}
          />
        </div>
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <ProductModal
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setEditingProductId(null);
          }}
          productId={editingProductId}
          onSuccess={() => {
            setShowProductModal(false);
            setEditingProductId(null);
            loadProducts();
          }}
        />
      )}

      {/* View Product Modal */}
      {showViewModal && selectedProduct && (
        <ViewProductModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
        />
      )}

      <style>{`
        .product-cell {
          display: flex;
          align-items: center;
          gap: var(--sp-3);
        }
        .product-thumb-wrap {
          position: relative;
          width: 44px;
          height: 44px;
          flex-shrink: 0;
          border-radius: var(--r-md);
          overflow: hidden;
          background: var(--neutral-100);
        }
        .product-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .product-thumb-placeholder {
          position: absolute;
          inset: 0;
          display: none;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          background: var(--neutral-100);
        }
        .product-thumb-placeholder.visible {
          display: flex;
        }
        .product-thumb-placeholder svg {
          width: 22px;
          height: 22px;
        }
        .product-info {
          min-width: 0;
        }
        .product-name {
          font-weight: var(--fw-semibold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .product-name-sub {
          font-size: var(--fs-xs);
          color: var(--text-muted);
        }
        .action-buttons {
          display: flex;
          gap: var(--sp-2);
        }
        .card-header-info {
          font-size: var(--fs-sm);
        }
        .products-store-filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: var(--sp-1);
          padding: var(--sp-3) var(--sp-5);
          border-bottom: 1px solid var(--border-light);
        }
        .products-store-filter-bar .status-tab {
          flex-shrink: 0;
          padding: 5px 14px;
          border-radius: var(--r-pill);
          border: 1.5px solid transparent;
          background: transparent;
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
        }
        .products-store-filter-bar .status-tab:hover:not(.status-tab-active) {
          background: var(--surface-2);
        }
        .products-brand-filter {
          display: flex;
          align-items: center;
          gap: var(--sp-2);
          margin-inline-start: auto;
        }
        .products-brand-filter .form-select {
          min-width: 220px;
          padding-block: 6px;
          font-size: var(--fs-sm);
        }
        @media (max-width: 640px) {
          .products-brand-filter { width: 100%; margin-inline-start: 0; }
          .products-brand-filter .form-select { flex: 1; min-width: 0; }
        }
        .products-store-filter-bar .status-tab-active {
          background: rgba(30, 58, 95, 0.1);
          color: var(--primary);
          border-color: rgba(30, 58, 95, 0.25);
          font-weight: 700;
        }
        @media (max-width: 768px) {
          .product-thumb-wrap {
            width: 36px;
            height: 36px;
          }
          .product-thumb-placeholder svg {
            width: 18px;
            height: 18px;
          }
          .action-buttons {
            flex-direction: column;
          }
          .action-buttons .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsIndex;
